import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Input } from "../components/ui/Fields";
import { Button } from "../components/ui/Button";
import { apiRequest, ApiError } from "../api/client";
import type { ChatConversationSummary, ChatMessage } from "../api/types";

/**
 * Deliberately not the same as a user-facing chat UI — every load here is
 * an explicit moderation action, audit-logged server-side with exactly
 * which admin viewed which user's messages. There's no passive "chat tab"
 * feel to this on purpose; it starts empty until a specific user id is
 * entered, so opening this page doesn't itself constitute browsing anyone's
 * messages.
 */
export function ChatModerationPage() {
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get("userId") ?? "");
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [selectedOtherUserId, setSelectedOtherUserId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get("userId")) loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadConversations() {
    if (!userId) return;
    setIsLoadingConversations(true);
    setError(null);
    setSelectedOtherUserId(null);
    setMessages([]);
    apiRequest<ChatConversationSummary[]>("/admin/chat/conversations", { params: { userId } })
      .then(setConversations)
      .catch((err) => setError(err instanceof ApiError ? err.message : "could not load conversations"))
      .finally(() => setIsLoadingConversations(false));
  }

  function loadThread(otherUserId: string) {
    setSelectedOtherUserId(otherUserId);
    setIsLoadingMessages(true);
    apiRequest<ChatMessage[]>("/admin/chat/messages", { params: { userId, otherUserId, range: "0-99" } })
      .then(setMessages)
      .catch((err) => setError(err instanceof ApiError ? err.message : "could not load messages"))
      .finally(() => setIsLoadingMessages(false));
  }

  return (
    <AppShell title="Chat Moderation">
      <p className="mb-4 text-xs text-ink-faint">
        Every conversation and message viewed here is recorded in the Audit Log with your admin account and a
        timestamp — this bypasses the normal per-user privacy restriction deliberately, for moderation purposes only.
      </p>

      <Card className="mb-4 p-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Input label="User ID to investigate" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Paste a user's UUID" />
          </div>
          <Button onClick={loadConversations} isLoading={isLoadingConversations}>
            Load Conversations
          </Button>
        </div>
      </Card>

      {error && <p className="mb-4 rounded-lg bg-danger-bg px-4 py-3 text-sm text-danger">{error}</p>}

      {conversations.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-4 lg:col-span-1">
            <h3 className="mb-3 text-sm font-semibold text-ink">Conversations ({conversations.length})</h3>
            <div className="space-y-1">
              {conversations.map((c) => (
                <button
                  key={c.other_user_id}
                  onClick={() => loadThread(c.other_user_id)}
                  className={`w-full rounded-lg p-2.5 text-left text-sm transition-colors ${
                    selectedOtherUserId === c.other_user_id ? "bg-gold/10 text-gold" : "text-ink-muted hover:bg-surface-raised"
                  }`}
                >
                  <p className="truncate font-mono text-xs">{c.other_user_id.slice(0, 12)}...</p>
                  <p className="truncate text-xs text-ink-faint">{c.last_message_body}</p>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4 lg:col-span-2">
            <h3 className="mb-3 text-sm font-semibold text-ink">Thread</h3>
            {!selectedOtherUserId ? (
              <p className="text-sm text-ink-faint">Select a conversation to view the thread.</p>
            ) : isLoadingMessages ? (
              <p className="text-sm text-ink-faint">Loading...</p>
            ) : (
              <div className="max-h-[500px] space-y-2 overflow-y-auto">
                {messages.map((m) => (
                  <div key={m.id} className={`max-w-[80%] rounded-lg p-2.5 text-sm ${m.sender_id === userId ? "ml-auto bg-gold/10" : "bg-surface-raised"}`}>
                    <p className="text-ink">{m.body}</p>
                    <p className="mt-1 text-[10px] text-ink-faint">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </AppShell>
  );
}
